/**
 * Run display titles, derived on this endpoint only (no plaintext reaches the relay):
 * the user's rename (sealed `runtitle` entity) → the agent's session title (from the decrypted
 * transcript) → the referenced task file's H1 (sealed run.start result) → the prompt heuristic.
 */
import { deriveRunTitle, RUN_TITLE_MAX, RunTitleEntry, runTitleEntityId } from '@viko/protocol'
import type { BrowserEntities } from './encrypted-entities.js'
import type { RunSummary } from './types.js'

type Entities = Pick<BrowserEntities, 'list' | 'write'>
const TTL_MS = 30_000

export type Titled = Pick<RunSummary, 'runId' | 'title' | 'prompt' | 'taskTitle'>

export function titleRun<T extends Titled>(
  row: T,
  userTitle: string | undefined,
  agentTitle: string | undefined,
): T {
  // Rows without their prompt (history, legacy hub rows) carry a first-prompt title: shorten it.
  const prompt = row.prompt ?? ((row.title?.length ?? 0) > RUN_TITLE_MAX ? row.title : undefined)
  const { title } = deriveRunTitle({
    userTitle,
    agentTitle,
    taskTitle: row.taskTitle,
    prompt,
  })
  return title && title !== row.title ? { ...row, title } : row
}

export class RunTitles {
  private cache: { entities: Entities; at: number; titles: Map<string, string> } | undefined

  async load(entities: Entities, now = Date.now()): Promise<Map<string, string>> {
    if (this.cache?.entities === entities && now - this.cache.at < TTL_MS) return this.cache.titles
    const titles = new Map<string, string>()
    for (const row of await entities.list('runtitle')) {
      const parsed = RunTitleEntry.safeParse(row.value)
      // The id is derived from the runId; a value naming another run is not trusted.
      if (!parsed.success || runTitleEntityId(parsed.data.runId) !== row.id) continue
      if (parsed.data.title.trim()) titles.set(parsed.data.runId, parsed.data.title)
    }
    this.cache = { entities, at: now, titles }
    return titles
  }

  /** An empty title clears the rename (back to the derived title). */
  async rename(entities: Entities, runId: string, title: string, now = Date.now()) {
    const entry = RunTitleEntry.parse({ runId, title: title.trim(), updatedAt: now })
    await entities.write('runtitle', runTitleEntityId(runId), entry)
    if (this.cache?.entities === entities) {
      if (entry.title) this.cache.titles.set(runId, entry.title)
      else this.cache.titles.delete(runId)
    }
  }

  apply<T extends Titled>(
    rows: T[],
    titles: Map<string, string>,
    agentTitle: (runId: string) => string | undefined,
  ): T[] {
    return rows.map((r) => titleRun(r, titles.get(r.runId), agentTitle(r.runId)))
  }
}
